import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { useToast } from "../lib/toast";
import { Tilt, onRippleClick } from "../lib/interactions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxesStacked } from "@fortawesome/free-solid-svg-icons";
import type { Category, Product } from "../lib/types";

const fmt = (n: number) => n.toLocaleString("ko-KR");

const PAGE = 60;

export default function Catalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [category, setCategory] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { add } = useCart();
  const { push } = useToast();

  // 250ms 디바운스로 불필요한 API 호출 방지
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    api.get<{ items: Category[] }>("/categories").then((r) => setCategories(r.items)).catch(console.error);
  }, []);

  const buildParams = (offset: number) => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
    params.set("limit", String(PAGE));
    params.set("offset", String(offset));
    return params.toString();
  };

  useEffect(() => {
    setLoading(true);
    api.get<{ items: Product[]; total: number }>(`/products?${buildParams(0)}`)
      .then((r) => { setProducts(r.items); setTotal(r.total ?? r.items.length); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, debouncedQ]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get<{ items: Product[]; total: number }>(`/products?${buildParams(products.length)}`);
      setProducts((prev) => [...prev, ...r.items]);
      setTotal(r.total ?? total);
    } finally { setLoadingMore(false); }
  };

  const filtered = useMemo(() => products, [products]);

  return (
    <main id="main-content" className="container" style={{ padding: "var(--s-8) var(--s-5)" }}>
      {/* 가로 스크롤 카테고리 바 (모바일 친화) */}
      <div className="cat-scroll" role="tablist" aria-label="카테고리">
        <button className={`cat-chip ${!category ? "is-active" : ""}`} onClick={() => setCategory(null)} role="tab" aria-selected={!category}>
          전체
        </button>
        {categories.map((c) => (
          <button key={c.id}
            className={`cat-chip ${category === c.slug ? "is-active" : ""}`}
            onClick={() => setCategory(c.slug)}
            role="tab" aria-selected={category === c.slug}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="row" style={{ margin: "var(--s-5) 0 var(--s-6)" }}>
        <input placeholder="상품명 / SKU / 브랜드 검색" value={q} onChange={(e) => setQ(e.target.value)} aria-label="검색" />
        <span className="muted" style={{ fontSize: 15, whiteSpace: "nowrap" }}>
          {loading ? "불러오는 중..." : `${filtered.length}${total > filtered.length ? ` / ${total}` : ""}개 상품`}
        </span>
      </div>

      <div className="product-grid">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="card" style={{ padding: "var(--s-4)" }}>
              <div className="skel" style={{ aspectRatio: "1/1", marginBottom: 8 }} />
              <div className="skel" style={{ width: "40%", height: 10, marginBottom: 8 }} />
              <div className="skel" style={{ width: "90%", height: 14, marginBottom: 6 }} />
              <div className="skel" style={{ width: "60%", height: 10, marginBottom: 12 }} />
              <div className="skel" style={{ width: "100%", height: 32 }} />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="card" style={{ padding: "var(--s-10)", gridColumn: "1 / -1", textAlign: "center" }}>
            <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>
              {q ? `"${q}"에 대한 결과가 없습니다` : "이 카테고리에 등록된 상품이 없습니다"}
            </p>
            <p className="muted" style={{ fontSize: 15, marginBottom: 16 }}>
              SKU·상품명·브랜드로 검색해 보세요. 다른 카테고리도 확인해 보세요.
            </p>
            <div className="row" style={{ gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              {q && <button className="btn btn-outline btn-sm" onClick={() => setQ("")}>검색어 지우기</button>}
              {category && <button className="btn btn-outline btn-sm" onClick={() => setCategory(null)}>전체 카테고리 보기</button>}
            </div>
          </div>
        ) : (
          filtered.map((p) => (
            // 카드 전체는 정적 컨테이너. Tilt 효과는 Link 영역에만 적용해
            // 버튼이 3D transform 중 hit-test에서 흔들리는 이슈를 방지.
            <div key={p.id} className="card" style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", gap: 8 }}>
              <Tilt max={5} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Link to={`/catalog/${encodeURIComponent(p.sku)}`} aria-label={`${p.name} 상세보기`} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div aria-hidden="true" style={{ aspectRatio: "1/1", background: "var(--bg-muted)", borderRadius: "var(--r-sm)", display: "grid", placeItems: "center", color: "var(--border-strong)", overflow: "hidden", position: "relative" }}>
                    {p.image_url ? (
                      <img src={p.image_url} alt="" loading="lazy"
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                    ) : (
                      <FontAwesomeIcon icon={faBoxesStacked} style={{ width: 48, height: 48 }} />
                    )}
                  </div>
                  <div>
                    <div className="mono muted" style={{ fontSize: 15, letterSpacing: "0.05em" }}>{p.sku}</div>
                    <div style={{ fontWeight: 600, fontSize: 15, marginTop: 2, lineHeight: 1.4 }}>{p.name}</div>
                    <div className="muted" style={{ fontSize: 15, marginTop: 2 }}>{p.brand} · {p.spec}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                    <div>
                      <span style={{ fontSize: 18, fontWeight: 700, color: "var(--primary)" }}>{fmt(p.price)}</span>
                      <span className="muted" style={{ fontSize: 15 }}>원/{p.unit}</span>
                    </div>
                    <span className={p.stock > 50 ? "badge badge-success" : p.stock > 0 ? "badge badge-warning" : "badge badge-danger"}>
                      {p.stock > 0 ? `재고 ${fmt(p.stock)}` : "품절"}
                    </span>
                  </div>
                </Link>
              </Tilt>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => { const ok = add(p, 1); push(ok ? `${p.name} 장바구니에 담았습니다` : `${p.name} 이미 장바구니에 있습니다`, ok ? "success" : "info"); }}
                disabled={p.stock === 0}
                onMouseDown={onRippleClick}
              >
                장바구니 담기
              </button>
            </div>
          ))
        )}
      </div>

      {!loading && filtered.length < total && (
        <div style={{ textAlign: "center", marginTop: "var(--s-6)" }}>
          <button className="btn btn-outline btn-lg" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "불러오는 중..." : `더 보기 (남은 ${total - filtered.length}개)`}
          </button>
        </div>
      )}

      <style>{`
        .cat-scroll {
          display: flex; gap: 8px;
          overflow-x: auto;
          padding: 8px 2px;
          margin: 0 -4px;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .cat-scroll::-webkit-scrollbar { display: none; }
        .cat-chip {
          flex: 0 0 auto;
          padding: 8px 16px;
          border-radius: var(--r-full);
          background: var(--surface);
          border: 1px solid var(--border);
          color: var(--text-2);
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
          scroll-snap-align: start;
          cursor: pointer;
          transition: all var(--dur-fast) var(--ease-out);
        }
        .cat-chip:hover { border-color: var(--primary); color: var(--text); }
        .cat-chip.is-active {
          background: var(--primary);
          color: #fff;
          border-color: var(--primary);
        }
        .product-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: var(--s-4);
        }
        @media (max-width: 480px) {
          .product-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); }
        }
      `}</style>
    </main>
  );
}
